import React, { useEffect } from "react";

import Button, { ButtonTypes } from "../../../../components/Button";
import { Skeleton } from "../../../../components/Skeleton";
import { SectionUnavailable } from "../../components/SectionUnavailable";
import { Caption, Card, StatusNote } from "../../styles";
import { DetailPane } from "../styles";
import { SelectionPaneProps } from "../types";
import { InstrumentDetail } from "./InstrumentDetail";

export const SelectionPane: React.FC<SelectionPaneProps> = ({
  selectedId,
  selection: { instrument, isHidden, isNotFound, isFailed, retry },
  isListSettled,
  onSelectionShown,
  detailRef,
  onClose,
  ...detailProps
}) => {
  const isShown = instrument !== null || isNotFound;

  // Waits for the list too, whose rows would otherwise push a stacked editor back out of view.
  // selectedId is a dependency because moving between listed rows leaves isShown true throughout.
  useEffect(() => {
    if (isShown && isListSettled) {
      onSelectionShown();
    }
  }, [selectedId, isShown, isListSettled, onSelectionShown]);

  const renderBody = () => {
    if (selectedId === null) {
      return detailProps.isStacked ? null : (
        <Card>
          <Caption>
            Select an instrument to edit its source country and pricing.
          </Caption>
        </Card>
      );
    }
    if (isNotFound) {
      return (
        <StatusNote role="status" $tone="warning">
          {`Instrument ${selectedId} was not found. `}
          <Button type={ButtonTypes.link} size="small" onClick={onClose}>
            Close
          </Button>
        </StatusNote>
      );
    }
    if (instrument) {
      return (
        <InstrumentDetail
          instrument={instrument}
          isHidden={isHidden}
          onClose={onClose}
          {...detailProps}
        />
      );
    }
    if (isFailed) {
      return (
        <Card aria-label="Selected instrument">
          <SectionUnavailable observedAt={null} onRetry={retry} />
        </Card>
      );
    }
    return <Skeleton variant="rectangular" height="12rem" />;
  };

  return (
    <DetailPane ref={detailRef} tabIndex={-1} aria-label="Selected instrument">
      {renderBody()}
    </DetailPane>
  );
};
